import { CSV_IMPORT_KEYS } from '../config/csvImportConfig';
import { createCompanyAddress } from '../models/companyModels';
import { normalizeSupplierTicker, resolveSupplierTickerFromRow } from './supplierTickerUtils';

const str = (value) => (value == null ? '' : String(value).trim());

const readInt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
};

const readNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const readBool = (value) => {
  if (value === true || value === false) return value;
  const s = str(value).toLowerCase();
  if (!s) return undefined;
  if (s === 'true' || s === '1' || s === 'yes') return true;
  if (s === 'false' || s === '0' || s === 'no') return false;
  return undefined;
};

const readList = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  const raw = str(value);
  if (!raw) return [];
  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw.replace(/'/g, '"'));
      return Array.isArray(parsed) ? parsed.map((v) => String(v).trim()).filter(Boolean) : [];
    } catch {
      /* fall through */
    }
  }
  return raw.split(/[;,]/).map((v) => v.trim()).filter(Boolean);
};

const normalizeAddressType = (facilityType, addressType) => {
  const candidate = str(addressType || facilityType).toLowerCase();
  const allowed = ['manufacturing', 'distribution', 'headquarters', 'office', 'warehouse', 'other'];
  if (allowed.includes(candidate)) return candidate;
  if (candidate.includes('manufact')) return 'manufacturing';
  if (candidate.includes('distrib')) return 'distribution';
  if (candidate.includes('warehouse')) return 'warehouse';
  if (candidate.includes('office')) return 'office';
  return 'manufacturing';
};

const buildLocation = (row) => {
  const existing = str(row.location);
  if (existing) return existing;
  const lat = readNum(row.latitude);
  const lng = readNum(row.longitude);
  if (lat != null && lng != null) {
    return `Point(${lat}, ${lng})`;
  }
  return '';
};

const mapTenantCompanyDto = (rows) => {
  const row = rows.find((r) => str(r.name)) || rows[0];
  if (!row) return null;
  const name = str(row.name);
  if (!name) return null;
  return {
    name,
    legal_name: str(row.legal_name) || undefined,
    duns_number: str(row.duns_number) || undefined,
    country: str(row.country) || undefined,
    city: str(row.city) || undefined,
    industry: str(row.industry) || undefined,
    annual_revenue: str(row.annual_revenue) || undefined,
    employee_count: readInt(row.employee_count),
    stock_ticker: str(row.stock_ticker) || undefined,
  };
};

const mapTenantFacilitiesDtos = (rows) =>
  rows
    .map((row) => {
      const name = str(row.facility_name) || str(row.name);
      if (!name && !str(row.facility_id)) return null;
      return {
        id: str(row.facility_id) || undefined,
        name: name || str(row.facility_id),
        type: str(row.facility_type) || undefined,
        address: str(row.address) || undefined,
        city: str(row.city) || undefined,
        country: str(row.country) || undefined,
        location: buildLocation(row) || undefined,
        capacity_units: str(row.capacity_units) || undefined,
        capacity_value: str(row.capacity_value) || undefined,
      };
    })
    .filter(Boolean);

const mapSupplierDtos = (rows) =>
  rows
    .map((row) => {
      const ticker = normalizeSupplierTicker(resolveSupplierTickerFromRow(row));
      if (!ticker) return null;
      const name = str(row.name) || ticker;
      const categories = readList(row.categories);
      return {
        ticker,
        name,
        description: str(row.description) || name,
        industry: str(row.industry) || 'General',
        tier: str(row.tier) || undefined,
        legal_name: str(row.legal_name) || str(row.legalName) || undefined,
        duns_number: str(row.duns_number) || str(row.dunsNumber) || undefined,
        country: str(row.country) || undefined,
        region: str(row.region) || undefined,
        categories: categories.length ? categories : undefined,
        criticality_score: readNum(row.criticality_score ?? row.criticalityScore),
        is_sole_source: readBool(row.is_sole_source ?? row.isSoleSource),
        is_strategic: readBool(row.is_strategic ?? row.isStrategic),
        uflpa_flag: readBool(row.uflpa_flag ?? row.uflpaFlag),
        annual_spend: str(row.annual_spend ?? row.annualSpend) || undefined,
        data_source: str(row.data_source ?? row.dataSource) || undefined,
        confidence_rating: str(row.confidence_rating ?? row.confidenceRating) || undefined,
        is_user_made: true,
      };
    })
    .filter(Boolean);

const mapSupplierFacilityDtos = (rows) =>
  rows
    .map((row) => {
      const ticker = normalizeSupplierTicker(resolveSupplierTickerFromRow(row));
      if (!ticker) return null;
      const street = str(row.street_address) || str(row.address);
      const city = str(row.city);
      const country = str(row.country);
      if (!street || !city || !country) return null;

      return createCompanyAddress({
        address_id: str(row.address_id) || undefined,
        ticker,
        facility_id: str(row.facility_id) || undefined,
        facility_name: str(row.facility_name) || undefined,
        street_address: street,
        city,
        state: str(row.state) || undefined,
        region: str(row.region) || undefined,
        postal_code: str(row.postal_code) || undefined,
        country,
        location: buildLocation(row) || undefined,
        latitude: readNum(row.latitude),
        longitude: readNum(row.longitude),
        address_type: normalizeAddressType(row.facility_type, row.address_type),
        facility_type: str(row.facility_type) || undefined,
        is_primary: readBool(row.is_primary),
        capacity_units: str(row.capacity_units) || undefined,
        capacity_value: str(row.capacity_value) || undefined,
        capacity_share: readNum(row.capacity_share),
        operational_status: str(row.operational_status) || undefined,
        headcount: readInt(row.headcount),
        hs_codes: readList(row.hs_codes),
        since: str(row.since) || undefined,
        certifications: readList(row.certifications),
        uflpa_territory: readBool(row.uflpa_territory),
        export_control_zone: str(row.export_control_zone) || undefined,
        last_audit_date: str(row.last_audit_date) || undefined,
        audit_outcome: str(row.audit_outcome) || undefined,
        insurance_coverage: readBool(row.insurance_coverage),
        natural_hazard_score: readNum(row.natural_hazard_score),
        single_site_risk: readBool(row.single_site_risk),
        lead_time_days: readInt(row.lead_time_days),
        primary_port: str(row.primary_port) || undefined,
        freight_mode: str(row.freight_mode) || undefined,
        avg_transit_days: readInt(row.avg_transit_days),
        annual_spend_share: readNum(row.annual_spend_share),
        last_verified_at: str(row.last_verified_at) || undefined,
        verified_by: str(row.verified_by) || undefined,
        data_source: str(row.data_source) || undefined,
        confidence_rating: str(row.confidence_rating) || undefined,
      }).toJSON();
    })
    .filter(Boolean);

const mapCommodityDtos = (rows) =>
  rows
    .map((row) => {
      const name = str(row.commodity) || str(row.name);
      if (!name) return null;
      const unit = str(row.unit);
      const category = str(row.category);
      const description = [str(row.description), category && `Category: ${category}`, unit && `Unit: ${unit}`]
        .filter(Boolean)
        .join(' · ');
      return {
        name,
        description: description || undefined,
        hsCode: str(row.hs_code) || undefined,
      };
    })
    .filter(Boolean);

const mapGeographyDtos = (rows) =>
  rows
    .map((row) => {
      const name = str(row.country) || str(row.city) || str(row.name);
      if (!name) return null;
      return {
        name,
        region: str(row.region) || undefined,
        exportControlStatus: str(row.risk_level) || undefined,
      };
    })
    .filter(Boolean);

const mapLogisticsDtos = (rows) =>
  rows
    .map((row) => {
      const name = str(row.route_name) || str(row.name);
      if (!name) return null;
      const origin = str(row.origin);
      const destination = str(row.destination);
      return {
        name,
        type: str(row.mode) || undefined,
        country: str(row.country) || undefined,
        location: [origin, destination].filter(Boolean).join(' → ') || undefined,
      };
    })
    .filter(Boolean);

const mapProductDtos = (rows) =>
  rows
    .map((row) => {
      const name = str(row.product_name) || str(row.name);
      if (!name && !str(row.sku)) return null;
      return {
        sku: str(row.sku) || undefined,
        name: name || str(row.sku),
        category: str(row.category) || undefined,
      };
    })
    .filter(Boolean);

const mapGraphRelationshipDtos = (rows) =>
  rows
    .map((row) => {
      const from = str(row.source);
      const to = str(row.target);
      const relationship = str(row.relationship_type) || str(row.relationship);
      if (!from || !to || !relationship) return null;
      const weight = str(row.weight);
      const notes = str(row.notes);
      return {
        from,
        to,
        relationship,
        description: [notes, weight && `weight=${weight}`].filter(Boolean).join(' · ') || undefined,
      };
    })
    .filter(Boolean);

const BUILDERS = {
  [CSV_IMPORT_KEYS.tenantCompany]: (rows) => {
    const dto = mapTenantCompanyDto(rows);
    return dto ? { kind: 'single', payload: dto } : { kind: 'single', payload: null };
  },
  [CSV_IMPORT_KEYS.facilities]: (rows) => ({ kind: 'list', payload: mapTenantFacilitiesDtos(rows) }),
  [CSV_IMPORT_KEYS.suppliers]: (rows) => ({ kind: 'list', payload: mapSupplierDtos(rows) }),
  [CSV_IMPORT_KEYS.supplierFacilities]: (rows) => ({
    kind: 'list',
    payload: mapSupplierFacilityDtos(rows),
  }),
  [CSV_IMPORT_KEYS.commodities]: (rows) => ({ kind: 'list', payload: mapCommodityDtos(rows) }),
  [CSV_IMPORT_KEYS.geographies]: (rows) => ({ kind: 'list', payload: mapGeographyDtos(rows) }),
  [CSV_IMPORT_KEYS.logistics]: (rows) => ({ kind: 'list', payload: mapLogisticsDtos(rows) }),
  [CSV_IMPORT_KEYS.products]: (rows) => ({ kind: 'list', payload: mapProductDtos(rows) }),
  [CSV_IMPORT_KEYS.graphOverview]: (rows) => ({
    kind: 'list',
    payload: mapGraphRelationshipDtos(rows),
  }),
};

/**
 * @param {string} importKey
 * @param {Record<string, string>[]} mappedRows
 */
export const buildBatchPayloadFromMappedRows = (importKey, mappedRows) => {
  const rows = Array.isArray(mappedRows) ? mappedRows : [];
  const builder = BUILDERS[importKey];
  if (!builder) {
    return { kind: 'unknown', payload: [] };
  }
  return builder(rows);
};
